import React from 'react'
import { message } from 'antd'
import { productApi } from 'api/product'
import { IProductResponse, IQuickUpdateProductRequest } from 'models/product'
import { showServerError } from 'shared/utils/showServerError'

interface UseQuickProductUpdateParams {
  setProducts: React.Dispatch<React.SetStateAction<IProductResponse[]>>
}

export const useQuickProductUpdate = ({
  setProducts
}: UseQuickProductUpdateParams) => {
  const [draftPrices, setDraftPrices] = React.useState<Record<string, string>>({})
  const [draftStocks, setDraftStocks] = React.useState<Record<string, string>>({})
  const [savingFields, setSavingFields] = React.useState<string[]>([])

  const getSavingFieldKey = React.useCallback(
    (productId: string, field: keyof IQuickUpdateProductRequest) =>
      `${productId}-${field}`,
    []
  )

  const toggleSavingField = React.useCallback(
    (
      productId: string,
      field: keyof IQuickUpdateProductRequest,
      isSaving: boolean
    ) => {
      const fieldKey = getSavingFieldKey(productId, field)

      setSavingFields((prev) =>
        isSaving
          ? prev.includes(fieldKey)
            ? prev
            : [...prev, fieldKey]
          : prev.filter((item) => item !== fieldKey)
      )
    },
    [getSavingFieldKey]
  )

  const isFieldSaving = React.useCallback(
    (productId: string, field: keyof IQuickUpdateProductRequest) =>
      savingFields.includes(getSavingFieldKey(productId, field)),
    [getSavingFieldKey, savingFields]
  )

  const parseInlineNumber = React.useCallback((value: string) => {
    const normalizedValue = value.replace(/\s/g, '').replace(',', '.')
    return Number(normalizedValue)
  }, [])

  const clearDraft = React.useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<Record<string, string>>>,
      productId: string
    ) => {
      setter((prev) => {
        const nextState = { ...prev }
        delete nextState[productId]
        return nextState
      })
    },
    []
  )

  const updateProductInList = React.useCallback(
    (
      productId: string,
      data: IQuickUpdateProductRequest,
      updatedProduct?: Partial<IProductResponse>
    ) => {
      setProducts((prev) =>
        prev.map((product) => {
          if (product._id !== productId) {
            return product
          }

          return {
            ...product,
            ...updatedProduct,
            ...(typeof data.price === 'number' && { price: data.price }),
            ...(typeof data.in_stock === 'number' && {
              dimension: {
                ...product.dimension,
                ...updatedProduct?.dimension,
                in_stock: data.in_stock
              }
            })
          }
        })
      )
    },
    [setProducts]
  )

  const saveQuickUpdate = React.useCallback(
    async (
      productId: string,
      field: keyof IQuickUpdateProductRequest,
      data: IQuickUpdateProductRequest,
      successMessage: string
    ) => {
      try {
        toggleSavingField(productId, field, true)
        const response = await productApi.quickUpdateProduct(productId, data)
        updateProductInList(productId, data, response.data as Partial<IProductResponse>)
        message.success(successMessage)
      } catch (error) {
        showServerError(error)
      } finally {
        toggleSavingField(productId, field, false)
      }
    },
    [toggleSavingField, updateProductInList]
  )

  const handlePriceBlur = React.useCallback(
    async ({ _id, price }: IProductResponse) => {
      const draftValue = draftPrices[_id]

      if (typeof draftValue === 'undefined') {
        return
      }

      const nextPrice = parseInlineNumber(draftValue)

      if (!draftValue.trim() || Number.isNaN(nextPrice) || nextPrice < 0) {
        clearDraft(setDraftPrices, _id)
        return message.error('Укажите корректную цену')
      }

      if (nextPrice === Number(price)) {
        clearDraft(setDraftPrices, _id)
        return
      }

      await saveQuickUpdate(_id, 'price', { price: nextPrice }, 'Цена обновлена')
      clearDraft(setDraftPrices, _id)
    },
    [clearDraft, draftPrices, parseInlineNumber, saveQuickUpdate]
  )

  const handleStockBlur = React.useCallback(
    async ({ _id, dimension }: IProductResponse) => {
      const draftValue = draftStocks[_id]

      if (typeof draftValue === 'undefined') {
        return
      }

      const nextStock = Number(draftValue)

      if (!draftValue.trim() || Number.isNaN(nextStock) || nextStock < 0) {
        clearDraft(setDraftStocks, _id)
        return message.error('Укажите корректный остаток')
      }

      if (nextStock === Number(dimension?.in_stock ?? 0)) {
        clearDraft(setDraftStocks, _id)
        return
      }

      await saveQuickUpdate(
        _id,
        'in_stock',
        { in_stock: nextStock },
        'Остатки обновлены'
      )
      clearDraft(setDraftStocks, _id)
    },
    [clearDraft, draftStocks, saveQuickUpdate]
  )

  return {
    draftPrices,
    draftStocks,
    setDraftPrices,
    setDraftStocks,
    isFieldSaving,
    handlePriceBlur,
    handleStockBlur
  }
}
